import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import hashPassword from 'utils/helpers/hash/hashPassword';
import { useLazyQuery, useMutation } from '@apollo/client';
import { REGISTER_USER } from 'entites/user/create-user.graphql';
import {
	LOGIN_USER_V2
} from 'entites/user/login-user.graphql';
import { AuthLayout } from './components/AuthLayout';
import { AUTH_STATE } from 'utils/enums/auth-state.enum';
import { handleRedirectRoute } from 'utils/helpers/handleRedirectRoute';
import { GET_USER_INFO } from 'entites/user/get-user-info.graphql';
import { handleSetData } from 'utils/helpers/log-in-log-out/handleSetData';
import useShowToast from 'hooks/useShowToast';
import { handleLogOut } from 'utils/helpers/log-in-log-out/handleLogOut';
import type { LoginCredentials, SignUpCredentials } from 'utils/types/user';
import { initialLogInValues } from 'constants/user';
import useQuery from 'hooks/useQuery';
import { USER_ROLE } from 'utils/enums/user-role.enum';
import { ampSetUserId, ampSetUserProperty } from 'utils/helpers/amplitude/amplitude';
import { API_V2 } from 'constants/graphql';
import { handleRole } from 'utils/helpers/handleRole';

const AuthorizationPage = () => {
	const { authState } = useParams();
  const [credentials, setCredentials] = useState<LoginCredentials>(initialLogInValues);
	const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false);
  const { email } = useQuery();
	const [signUpStep, setSignUpStep] = useState(1);
	const navigate = useNavigate();
	const { showErrorToast } = useShowToast();

	const [getUserInfo] = useLazyQuery(GET_USER_INFO, {
		fetchPolicy: 'network-only',
	});

	const [createUser, { loading: isUserCreationLoading }] = useMutation(
		REGISTER_USER,
		{
			context: {
				clientName: API_V2,
			},
		},
	);
	const [loginUser, { loading: isLoginLoading }] = useMutation(
		LOGIN_USER_V2,
		{
			context: {
				clientName: API_V2,
			},
		},
	);

	const changeSignUpStep = useCallback((step: number) => {
		setSignUpStep(step);
	}, []);

	const handleSetCredentials = useCallback((data: LoginCredentials) => {
		setCredentials(data);
	}, []);

	const fetchUserInfo = useCallback(async () => {
		if (hasAttemptedLogin) return;

		setHasAttemptedLogin(true);
		const { data: userData } = await getUserInfo();

		if (userData.getUserInfo.isBlocked) {
			handleLogOut(false);
			
			navigate('/permission-denied', { state: { user_id: userData.getUserInfo.user_id}});
			return;
		}

		if (userData) {
			handleSetData({
				user_id: userData.getUserInfo.user_id,
				email: userData.getUserInfo.email,
				role: userData.getUserInfo.role,
			});

			if (userData.getUserInfo.role === USER_ROLE.WRITER
				|| userData.getUserInfo.role === USER_ROLE.SOURCER) {
				ampSetUserId(userData.getUserInfo.user_id);
				ampSetUserProperty('email', userData.getUserInfo.email);
			}
		}
		const flowPath = handleRedirectRoute();

		navigate(flowPath);
	}, [getUserInfo, navigate]);

	const handleUserLogin = useCallback(
		async ({ email, password }: LoginCredentials) => {
			try {
				const { data } = await loginUser({
					variables: {
						credentials: {
							email,
							password,
						},
					},
				});

				if (data?.login?.success) {
					const { data: userData } = await getUserInfo();

					if (userData.getUserInfo.isBlocked) {
						handleLogOut(false);
						
						navigate('/permission-denied', { state: { user_id: userData.getUserInfo.user_id}});
						return;
					}

					if (userData) {
						handleSetData({
							user_id: userData.getUserInfo.user_id,
							email: userData.getUserInfo.email,
							role: userData.getUserInfo.role,
						});

						if (userData.getUserInfo.role === USER_ROLE.WRITER
							|| userData.getUserInfo.role === USER_ROLE.SOURCER) {
							ampSetUserId(userData.getUserInfo.user_id);
							ampSetUserProperty('email', userData.getUserInfo.email);
						}
					}
				}

				const flowPath = handleRedirectRoute();

				navigate(flowPath);
			} catch (error) {
				showErrorToast({
          id: "logInError",
          title: "Sorry something went wrong",
          reason: "Log in error",
        });
			}
		},
		[loginUser],
	);

	const handleLogin = useCallback(
		async (values: LoginCredentials) => {
			const hashedPassword = await hashPassword(values.password);

			await handleUserLogin({
				email: values.email,
				password: hashedPassword,
			});
		},
		[handleUserLogin],
	);

	const loginViaGoogleHandler = useCallback(() => {
		window.open(`${process.env.REACT_APP_ORIGIN_SERVER_URL}api/auth/google`, '_top');
	}, []);

	const loginViaFacebookHandler = useCallback(() => {
		window.open(`${process.env.REACT_APP_ORIGIN_SERVER_URL}api/auth/facebook`, '_top');
	}, []);

	const handleCreateAccount = useCallback(
		async (values: SignUpCredentials) => {
			try {
				const hashedPassword: string = await hashPassword(credentials.password);

				await createUser({
					variables: {
						user: {
							email: credentials.email,
							firstName: values.first_name,
							lastName: values.last_name,
							penName: values.pen_name,
							password: hashedPassword,
						},
					},
				});
				await handleUserLogin({
					email: credentials.email,
					password: hashedPassword,
				});
			} catch (error) {
				showErrorToast({
          id: "CreateAccountError",
          title: "Sorry something went wrong",
          reason: "Create account error",
        });
			}
		},
		[createUser, handleUserLogin, credentials],
	);

	useEffect(() => {
		if (email) {
			setCredentials({
				...initialLogInValues,
				email,
			});
		}
	}, [email]);

	useEffect(() => {
		const roles = handleRole();
		const hasRole = roles.writer || roles.admin || roles.moderator;
		if (authState === AUTH_STATE.LOG_IN && !hasAttemptedLogin && hasRole) {
			fetchUserInfo();
		}
	}, [authState, fetchUserInfo, hasAttemptedLogin]);

	return (
		<AuthLayout
			authState={authState as AUTH_STATE}
			credentials={credentials}
			handleSetCredentials={handleSetCredentials}
			signUpStep={signUpStep}
			changeSignUpStep={changeSignUpStep}
			handleLogin={handleLogin}
			isLoginLoading={isLoginLoading}
			isUserCreationLoading={isUserCreationLoading}
			loginViaGoogleHandler={loginViaGoogleHandler}
			loginViaFacebookHandler={
				loginViaFacebookHandler
			}
			handleCreateAccount={handleCreateAccount}
		/>
	);
};

export default AuthorizationPage;
